// PublicStoreVideos — /marketplace/store/:slug/videos
// Dedicated "All Videos" page for store promo videos
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Play } from 'lucide-react';
import api from '../utils/api';

const API = process.env.REACT_APP_BACKEND_URL || '';
const abs = (u) => (!u ? '' : u.startsWith('http') ? u : `${API}${u}`);

const ytEmbed = (u) => {
  if (!u) return null;
  const m = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};
const igEmbed = (u) => {
  if (!u) return null;
  const m = u.match(/instagram\.com\/(p|reel|tv)\/([\w-]+)/);
  if (!m) return null;
  return `https://www.instagram.com/${m[1]}/${m[2]}/embed/`;
};
const ytThumb = (u) => {
  if (!u) return null;
  const m = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : null;
};

export default function PublicStoreVideos() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    api.get(`/marketplace/store/public/${slug}`)
      .then((r) => setStore(r.data?.store || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
      ⏳ Loading videos…
    </div>
  );

  if (!store) return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6 text-center">
      <div>
        <div className="text-4xl mb-3">🎬</div>
        <div className="opacity-70">Store nahi mila.</div>
        <Link to="/marketplace" className="inline-block mt-3 px-4 py-2 rounded-xl bg-purple-500 text-white text-xs font-black">← Back</Link>
      </div>
    </div>
  );

  const videos = (store.promo_videos || []).filter(Boolean);

  if (videos.length === 0) return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6 text-center">
      <div>
        <div className="text-4xl mb-3">📭</div>
        <div className="opacity-70">Koi promo video nahi hai abhi.</div>
        <Link to={`/marketplace/store/${slug}`} className="inline-block mt-3 px-4 py-2 rounded-xl bg-purple-500 text-white text-xs font-black">← Back to Store</Link>
      </div>
    </div>
  );

  const active = videos[activeIdx];
  const embedUrl = ytEmbed(active);
  const igUrl = igEmbed(active);
  const thumb = ytThumb(active);

  return (
    <div className="min-h-screen text-white pb-16"
         style={{ background: 'linear-gradient(180deg, #1E1B4B 0%, #0f172a 60%, #020617 100%)' }}
         data-testid="store-videos-page">

      {/* Header */}
      <div className="sticky top-0 z-30 px-3 py-2.5 flex items-center gap-3 border-b border-slate-800"
           style={{ background: 'rgba(15,23,42,0.95)', backdropFilter: 'blur(12px)' }}>
        <Link to={`/marketplace/store/${slug}`} data-testid="store-videos-back"
              className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-white shrink-0">
          <ChevronLeft size={18} />
        </Link>
        <div className="min-w-0">
          <div className="text-sm font-black truncate">{store.shop_name}</div>
          <div className="text-[10px] opacity-60">🎬 {videos.length} Promo Videos</div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-3 pt-3 space-y-3">

        {/* Main video player */}
        <div className="rounded-xl overflow-hidden border border-rose-400/30 bg-black" data-testid="store-videos-main-player">
          {embedUrl
            ? <iframe title="main-video" src={`${embedUrl}?autoplay=0`} className="w-full aspect-video"
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            : igUrl
              ? <div className="relative">
                  <iframe title="ig-reel-main" src={igUrl} className="w-full"
                          style={{ minHeight: '540px', border: 'none' }}
                          scrolling="no" allowTransparency="true" />
                  <a href={active} target="_blank" rel="noreferrer"
                     className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/70 text-white text-[9px] font-black">
                    📸 Open on Instagram
                  </a>
                </div>
              : <video controls src={abs(active)} className="w-full aspect-video bg-black" playsInline />
          }
        </div>

        {/* Video number indicator */}
        <div className="text-center text-[10px] opacity-60 font-black">
          Video {activeIdx + 1} of {videos.length}
        </div>

        {/* Thumbnail strip / playlist */}
        {videos.length > 1 && (
          <div className="rounded-xl bg-slate-900/70 border border-slate-700 p-2">
            <div className="text-[10px] font-black text-slate-400 mb-2 uppercase tracking-wider px-1">All Videos</div>
            <div className="space-y-1.5">
              {videos.map((url, i) => {
                const th = ytThumb(url);
                const isYT = !!ytEmbed(url);
                const isIG = !!igEmbed(url);
                const isActive = i === activeIdx;
                return (
                  <button key={i} onClick={() => setActiveIdx(i)}
                          data-testid={`store-video-thumb-${i}`}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition ${isActive ? 'bg-rose-600/30 border border-rose-400/40' : 'bg-slate-800/60 hover:bg-slate-700/60 border border-transparent'}`}>
                    {/* Thumbnail */}
                    <div className="w-20 h-12 rounded-lg overflow-hidden shrink-0 bg-slate-700 border border-slate-600 relative">
                      {th
                        ? <img src={th} alt="" className="w-full h-full object-cover" />
                        : isIG
                          ? <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)' }}>
                              <span className="text-lg">📸</span>
                            </div>
                          : <div className="w-full h-full flex items-center justify-center bg-slate-700">
                              <span className="text-lg">🎬</span>
                            </div>
                      }
                      {isActive && (
                        <div className="absolute inset-0 bg-rose-500/20 flex items-center justify-center">
                          <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                            <Play size={12} className="text-slate-900 ml-0.5" />
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Label */}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-black text-white">
                        {isYT ? '▶ YouTube Video' : isIG ? '📸 Instagram Reel' : '🎬 Video'} #{i + 1}
                      </div>
                      <div className="text-[9px] opacity-50 truncate">{url}</div>
                    </div>
                    {isActive && <span className="text-[9px] text-rose-300 font-black shrink-0">Playing</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Back to store */}
        <Link to={`/marketplace/store/${slug}`}
              className="flex items-center justify-center gap-2 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-sm font-black transition"
              data-testid="store-videos-back-btn">
          ← Back to {store.shop_name}
        </Link>
      </div>
    </div>
  );
}
